import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, howToSchema, toolSchema } from "@/lib/schema";

const PalBracket = lazy(() => import("@/components/games/PalBracket").then((m) => ({ default: m.PalBracket })));
const AchievementWall = lazy(() => import("@/components/games/AchievementWall").then((m) => ({ default: m.AchievementWall })));

const TITLE = "Best Palworld Pal Bracket — Daily 16-Pal Vote";
const DESCRIPTION =
  "Vote the best Palworld Pal in a free 16-Pal knockout bracket: a fresh field every day at 00:00 UTC, four rounds of matchups and a shareable champion card.";
const OG_IMAGE = "https://palstars.com/og/palworld-games.jpg";

const STEPS = [
  { name: "Open today's bracket", text: "The 16-Pal field is seeded from the UTC date, so everyone who plays today votes on exactly the same bracket." },
  { name: "Pick every matchup", text: "Work through the round of 16, quarter-finals and semi-finals — each card shows the Pal's element and PvE rating to help you judge." },
  { name: "Crown a champion", text: "Win the final and your champion is displayed with its artwork and stats." },
  { name: "Share and compare", text: "Share the champion link and see whether your friends' brackets ended the same way." },
];

const FAQ: FaqItem[] = [
  { q: "What is the best Palworld Pal bracket?", a: "It is a free daily knockout bracket on Pal Stars: 16 Pals, four rounds and one champion. The field is seeded from the UTC date so every visitor votes the same matchups on the same day." },
  { q: "How is the 16-Pal field chosen?", a: "Uncommon, Rare and Legendary Pals from the Pal Stars database are drawn with a date-seeded shuffle, which keeps the bracket varied without ever repeating a Pal inside one field." },
  { q: "Is the bracket a community vote?", a: "Each bracket is your own run, and the share link lets you compare champions with friends. Your picks stay in your browser and never affect anyone else's field." },
  { q: "Does the bracket give packs or spins?", a: "No. Crowning a champion unlocks a cosmetic badge only, and brackets are capped at 20 per browser per UTC day." },
  { q: "When does the Palworld bracket reset?", a: "At 00:00 UTC, alongside the daily Pal Puzzle, the free booster packs and the Palsino spin claim." },
];

export const Route = createFileRoute("/pal-bracket")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Best Palworld Pal bracket — daily 16-Pal knockout" },
      { property: "og:description", content: "Four rounds, 16 Pals, one champion — a fresh Palworld bracket every day." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/pal-bracket" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/pal-bracket" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld Games", "/palworld-games"], ["Best Pal Bracket", "/pal-bracket"]]),
      toolSchema({ name: "Best Palworld Pal Bracket", description: DESCRIPTION, path: "/pal-bracket" }),
      howToSchema({ name: "How to play the Palworld Pal bracket", description: "Open today's field, pick every matchup, crown a champion and share it.", path: "/pal-bracket", steps: STEPS }),
      faqSchema(FAQ, "https://palstars.com/pal-bracket"),
    ],
  }),
  component: PalBracketPage,
});

function PalBracketPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars Arcade · daily field</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Best Palworld Pal bracket</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          Sixteen Palworld Pals enter a knockout bracket that is reseeded every day at 00:00 UTC. Pick a winner in every
          matchup across four rounds, crown your champion of Palpagos and share the result.
        </p>
      </header>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Best Palworld Pal bracket</h2><p className="mt-2 text-sm text-muted-foreground">Loading today's field…</p></section>}>
        <PalBracket />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">How the bracket works</h2>
        <ol className="mt-3 space-y-3">
          {STEPS.map((s, i) => (
            <li key={s.name} id={`step-${i + 1}`} className="rounded-lg border border-border/60 p-3">
              <p className="text-sm font-semibold">{i + 1}. {s.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Achievement badges</h2><p className="mt-2 text-sm text-muted-foreground">Loading badges…</p></section>}>
        <AchievementWall game="pal-bracket" heading="Bracket badges" />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Play next</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li><Link to="/palworld-games" className="font-semibold text-primary hover:underline">All free Palworld games →</Link></li>
          <li><Link to="/tier-list" className="font-semibold text-primary hover:underline">Palworld tier list →</Link> how the meta really ranks them.</li>
          <li><Link to="/pal-draft" className="font-semibold text-primary hover:underline">Palworld random team generator →</Link></li>
          <li><Link to="/leaderboard" className="font-semibold text-primary hover:underline">Weekly leaderboard →</Link></li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld Pal bracket FAQ" />
    </div>
  );
}
